import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, message } from 'antd';
import { TeamOutlined, UserOutlined, MessageOutlined, TrophyOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    activeTeams: 0,
    totalPlayers: 0,
    totalMessages: 0,
    totalTeams: 0,
    teamsPerDay: [],
    messagesPerDay: []
  });
  const [loading, setLoading] = useState(true);

  // Função para gerar ticks dinâmicos
  const generateTicks = (data) => {
    if (!data || data.length === 0) return [0, 2, 4, 6, 8, 10];

    const margin = 1.25;
    const maxValue = Math.max(...data.map(item => parseInt(item.count) || 0)) * margin;
    const maxTick = Math.ceil(maxValue);

    // Calcular intervalo mais legível baseado no valor máximo
    const interval = Math.ceil(maxTick / 7);

    const ticks = [];
    for (let i = 0; i <= maxTick; i += interval) {
      ticks.push(Math.round(i));
    }

    return ticks;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      message.error('Erro ao carregar estatísticas');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Painel de Controle</h2>
      
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total de Times"
              value={stats.totalTeams}
              prefix={<TrophyOutlined style={{ color: '#FA8C16' }} />}
              loading={loading}
              formatter={(value) => value.toLocaleString('pt-BR')}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Times Ativos"
              value={stats.activeTeams}
              prefix={<TeamOutlined style={{ color: '#00B96B' }} />}
              loading={loading}
              formatter={(value) => value.toLocaleString('pt-BR')}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Jogadores Cadastrados"
              value={stats.totalPlayers}
              prefix={<UserOutlined style={{ color: '#52C41A' }} />}
              loading={loading}
              formatter={(value) => value.toLocaleString('pt-BR')}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Convocações Enviadas"
              value={stats.totalMessages}
              prefix={<MessageOutlined style={{ color: '#1890FF' }} />}
              loading={loading}
              formatter={(value) => value.toLocaleString('pt-BR')}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card 
            title={`Times Criados por Dia (Últimos 30 dias) - Total: ${(stats.teamsPerDay || []).reduce((sum, item) => sum + parseInt(item.count), 0).toLocaleString('pt-BR')}`} 
            loading={loading}
          >
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={stats.teamsPerDay} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                />
                <YAxis 
                  domain={[0, (dataMax) => Math.ceil(dataMax)]} 
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  type="number"
                  scale="linear"
                  ticks={generateTicks(stats.teamsPerDay)}
                />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                  formatter={(value) => [value, 'Times Criados']}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#00B96B" 
                  strokeWidth={2}
                  dot={{ fill: '#00B96B' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card 
            title={`Convocações por Dia (Últimos 30 dias) - Total: ${(stats.messagesPerDay || []).reduce((sum, item) => sum + parseInt(item.count), 0).toLocaleString('pt-BR')}`} 
            loading={loading}
          >
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={stats.messagesPerDay || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                />
                <YAxis 
                  domain={[0, (dataMax) => Math.ceil(dataMax)]} 
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  type="number"
                  scale="linear"
                  ticks={generateTicks(stats.messagesPerDay)}
                />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                  formatter={(value) => [value, 'Convocações Enviadas']}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#1890FF" 
                  strokeWidth={2}
                  dot={{ fill: '#1890FF' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;